import React, { useEffect } from 'react';
import { Box, Grid2, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTranslation } from 'react-i18next';
import BlockWithTitle from '../../components/atom/BlockWithTitle';

function AvailableSeatInput({ count, setCount, max, disabled }) {
    const [tempCount, setTempCount] = React.useState(count);

    useEffect(() => {
        setTempCount(count);
    }, [count]);

    return (
        <input
            type="number"
            min={0}
            max={max > 0 ? max : undefined}
            disabled={disabled}
            value={tempCount}
            onBlur={() => {
                const parsed = Math.max(0, parseInt(tempCount, 10) || 0);
                setCount(max > 0 ? Math.min(max, parsed) : parsed);
            }}
            onChange={(ev) => {
                setTempCount(ev.target.value);
            }}
            style={{
                width: 48,
                textAlign: 'center',
                border: 'none',
                background: 'transparent',
                fontSize: 14,
                fontWeight: tempCount > 0 ? 600 : 400,
            }}
        />
    );
}

/**
 * Per-ticket-type reserved-capacity editor shared by the single-edit and
 * bulk-add timeslot pages. `value` is the ticketTypeReservedCapacity map
 * ({ [ticketTypeId]: reservedCount }); `onChange` receives the next map.
 */
function CapacityReservationEditor({ ticketTypes, value, onChange }) {
    const { t } = useTranslation();

    return (
        <BlockWithTitle title={t('timeslots.capacityReservations')}>
            <Grid2 size={{ xs: 12 }}>
                <Box
                    sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1,
                            py: 0.25,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'action.hover',
                        }}
                    >
                        <Box sx={{ flex: 1 }} />
                        <Box
                            sx={{
                                visibility: 'hidden',
                                display: 'inline-flex',
                            }}
                        >
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                <RemoveIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                width: 48,
                                textAlign: 'center',
                                color: 'text.secondary',
                                fontWeight: 600,
                            }}
                        >
                            {t('timeslots.bookableHeader')}
                        </Typography>
                        <Box
                            sx={{
                                visibility: 'hidden',
                                display: 'inline-flex',
                            }}
                        >
                            <IconButton size="small" sx={{ p: 0.25 }}>
                                <AddIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                ml: 1,
                                minWidth: 50,
                                textAlign: 'right',
                                color: 'text.secondary',
                                fontWeight: 600,
                            }}
                        >
                            {t('timeslots.totalHeader')}
                        </Typography>
                    </Box>
                    {ticketTypes.map((ticketType, idx) => {
                        const reserved = value?.[ticketType.id] || 0;
                        const blockCount = ticketType.blockLimit ?? ticketType.seats?.length ?? 0;
                        const available = Math.max(0, blockCount - reserved);
                        const setAvailable = (newAvailable) => {
                            const bounded = Math.max(0, Math.min(blockCount, newAvailable));
                            onChange({
                                ...value,
                                [ticketType.id]: blockCount - bounded,
                            });
                        };
                        return (
                            <Box
                                key={ticketType.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1,
                                    py: 0.25,
                                    borderTop: idx > 0 ? '1px solid' : 'none',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="body2" sx={{ flex: 1, textAlign: 'left' }}>
                                    {ticketType.name}
                                </Typography>
                                <IconButton
                                    size="small"
                                    disabled={blockCount === 0 || available === 0}
                                    onClick={() => setAvailable(available - 1)}
                                    sx={{ p: 0.25 }}
                                >
                                    <RemoveIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <AvailableSeatInput
                                    count={available}
                                    setCount={setAvailable}
                                    max={blockCount}
                                    disabled={blockCount === 0}
                                />
                                <IconButton
                                    size="small"
                                    disabled={blockCount === 0 || available >= blockCount}
                                    onClick={() => setAvailable(available + 1)}
                                    sx={{ p: 0.25 }}
                                >
                                    <AddIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        ml: 1,
                                        minWidth: 50,
                                        color: 'text.secondary',
                                        whiteSpace: 'nowrap',
                                        textAlign: 'right',
                                        fontVariantNumeric: 'tabular-nums',
                                    }}
                                >
                                    {blockCount}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Grid2>
            <Grid2 size={{ xs: 12 }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    style={{
                        marginTop: 4,
                        display: 'block',
                        textAlign: 'left',
                    }}
                >
                    {t('timeslots.capacityReservationsInfo')}
                </Typography>
            </Grid2>
        </BlockWithTitle>
    );
}

export default CapacityReservationEditor;
